import React, { Component, ErrorInfo, ReactNode } from "react";
import { Box, Typography, Button, Container } from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import WifiOffIcon from "@mui/icons-material/WifiOff";

interface Props {
  children: ReactNode;
  /** Optional: called instead of window.location.href = "/" on reset.
   *  Pass a React Router navigate function to avoid a hard reload. */
  onReset?: () => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  isChunkError: boolean;
}

// Detects if the error is caused by a failed lazy-load chunk (e.g., navigating
// to an unvisited route while offline). These are NOT real app crashes.
const isChunkLoadError = (error: Error): boolean => {
  const msg = error?.message || "";
  return (
    msg.includes("Loading chunk") ||
    msg.includes("Loading CSS chunk") ||
    msg.includes("ChunkLoadError") ||
    error?.name === "ChunkLoadError"
  );
};

class ErrorBoundary extends Component<Props, State> {
  // eslint-disable-next-line react/static-property-placement
  static defaultProps = {
    onReset: undefined,
  };

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      isChunkError: false,
    };
  }

  public static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      isChunkError: isChunkLoadError(error),
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (isChunkLoadError(error)) {
      console.warn(
        "ChunkLoadError: Page chunk failed to load (offline?):",
        error.message,
      );
    } else {
      console.error("Uncaught error:", error, errorInfo);
    }
  }

  private handleReset = () => {
    const { onReset } = this.props;
    this.setState({ hasError: false, error: null, isChunkError: false });
    if (onReset) {
      onReset(); // soft React Router navigation — no hard reload
    } else {
      window.location.href = "/"; // fallback for real crashes
    }
  };

  private handleRetry = () => {
    this.setState({ hasError: false, error: null, isChunkError: false });
    window.location.reload();
  };

  public render() {
    const { hasError, error, isChunkError } = this.state;
    const { children } = this.props;

    if (hasError) {
      // ── Offline / chunk-load failure ──────────────────────────────────────
      if (isChunkError) {
        const isActuallyOffline = !navigator.onLine;
        return (
          <Container maxWidth="sm">
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100vh",
                textAlign: "center",
                gap: 3,
              }}
            >
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  backgroundColor: "rgba(77, 18, 119, 0.1)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 1,
                }}
              >
                <WifiOffIcon sx={{ fontSize: 40, color: "#4D1277" }} />
              </Box>
              <Typography
                variant="h4"
                sx={{ fontWeight: 600, color: "#1e293b" }}
              >
                {isActuallyOffline
                  ? "You appear to be offline"
                  : "Page failed to load"}
              </Typography>
              <Typography sx={{ color: "#64748b", mb: 2 }}>
                {isActuallyOffline
                  ? "This page couldn't load because your internet connection is unavailable. Please check your network and try again."
                  : "This page couldn't load. This may be caused by a recent update or a temporary server issue. Try refreshing."}
              </Typography>
              <Box sx={{ display: "flex", gap: 2 }}>
                <Button
                  variant="contained"
                  onClick={this.handleRetry}
                  sx={{
                    backgroundColor: "#4D1277",
                    "&:hover": { backgroundColor: "#3b0f5c" },
                    px: 4,
                    py: 1,
                    borderRadius: "10px",
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  Retry
                </Button>
                <Button
                  variant="outlined"
                  onClick={this.handleReset}
                  sx={{
                    borderColor: "#4D1277",
                    color: "#4D1277",
                    "&:hover": { borderColor: "#3b0f5c", color: "#3b0f5c" },
                    px: 4,
                    py: 1,
                    borderRadius: "10px",
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  Back to Dashboard
                </Button>
              </Box>
            </Box>
          </Container>
        );
      }

      // ── Real app crash ────────────────────────────────────────────────────
      return (
        <Container maxWidth="sm">
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100vh",
              textAlign: "center",
              gap: 3,
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                backgroundColor: "rgba(77, 18, 119, 0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 1,
              }}
            >
              <WarningAmberIcon sx={{ fontSize: 40, color: "#4D1277" }} />
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 600, color: "#1e293b" }}>
              Something went wrong
            </Typography>
            <Typography sx={{ color: "#64748b", mb: 2 }}>
              An unexpected error occurred. We&apos;ve been notified and are
              looking into it.
            </Typography>
            {process.env.NODE_ENV === "development" && (
              <Box
                sx={{
                  textAlign: "left",
                  backgroundColor: "#f8fafc",
                  p: 2,
                  borderRadius: 1,
                  width: "100%",
                  overflow: "auto",
                  maxHeight: "200px",
                  border: "1px solid #e2e8f0",
                }}
              >
                <code style={{ fontSize: "12px", color: "#ef4444" }}>
                  {error?.toString()}
                </code>
              </Box>
            )}
            <Button
              variant="contained"
              onClick={this.handleReset}
              sx={{
                backgroundColor: "#4D1277",
                "&:hover": { backgroundColor: "#3b0f5c" },
                px: 4,
                py: 1,
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              Back to Dashboard
            </Button>
          </Box>
        </Container>
      );
    }

    return children;
  }
}

export default ErrorBoundary;
